class BasicHeader extends HTMLElement {
  constructor() {
    super();
  }

  get headerSection() {
    return document.querySelector(".header-section");
  }

  get enableTransparent() {
    return this.dataset.enableTransparent === "true";
  }

  connectedCallback() {
    this.init();
    if (window.ResizeObserver) {
      new ResizeObserver(this.setHeight.bind(this)).observe(this);
    }

    if (Shopify.designMode) {
      const section = this.closest(".shopify-section");
      section.addEventListener("shopify:section:load", this.init.bind(this));
      section.addEventListener("shopify:section:unload", this.init.bind(this));
      section.addEventListener("shopify:section:reorder", this.init.bind(this));
    }
  }

  init() {
    new FoxTheme.delayUntilInteraction(this.setHeight.bind(this));

    if (this.enableTransparent) {
      this.headerSection.classList.add("header-transparent");
    }
  }

  setHeight() {
    requestAnimationFrame(() => {
      // Cache layout computations to reduce reflow
      const offsetHeight = Math.round(this.offsetHeight);
      const offsetTop = Math.round(this.parentElement.offsetTop);

      // Batch style updates
      document.documentElement.style.setProperty(
        "--header-height",
        `${offsetHeight}px`,
      );
      document.documentElement.style.setProperty(
        "--header-offset-top",
        `${offsetTop}px`,
      );
    });
  }
}

customElements.define("basic-header", BasicHeader, { extends: "header" });

class StickyHeader extends BasicHeader {
  constructor() {
    super();

    this.classes = {
      pinned: "header-pinned",
    };
  }

  get isAlwaysSticky() {
    return this.dataset.stickyType === "always";
  }

  connectedCallback() {
    super.connectedCallback();

    this.currentScrollTop = 0;
    this.firstScrollTop = window.scrollY;

    // Cache initial bounds for better performance
    this.headerBounds = this.headerSection.getBoundingClientRect();

    this.beforeInit();
  }

  beforeInit() {
    this.headerSection.classList.add("header-sticky");
    this.headerSection.dataset.stickyType = this.dataset.stickyType;
    window.addEventListener("scroll", this.onScrollHandler.bind(this), false);
  }

  onScrollHandler() {
    const scrollTop = window.scrollY;
    const headerSection = this.headerSection;

    // Avoid layout thrashing by caching values
    const headerBoundsTop = this.headerBounds.top + this.firstScrollTop;
    const headerBoundsBottom = this.headerBounds.bottom + this.firstScrollTop;

    // Utilize batch CSS classes and updates
    requestAnimationFrame(() => {
      if (scrollTop > headerBoundsTop) {
        headerSection.classList.add("header-scrolled");

        if (this.isAlwaysSticky) {
          document.body.classList.add(this.classes.pinned);
        } else {
          // Sticky on scroll up.
          if (
            scrollTop < this.currentScrollTop ||
            scrollTop < headerBoundsBottom + 100
          ) {
            document.body.classList.add(this.classes.pinned);
          } else {
            document.body.classList.remove(this.classes.pinned);
          }
        }
      } else {
        headerSection.classList.remove("header-scrolled");
        document.body.classList.remove(this.classes.pinned);
      }

      // Compute the currently-visible height of the pinned header and write
      // it into --header-height. Once the announcement bar (sits above the
      // sticky header section in the DOM flow) has scrolled off viewport
      // this drops from (announcement + nav) to just the nav wrapper height.
      // This keeps in sync with the sticky offsets / viewport-fill heights
      // consumers set using --header-height.
      const pinned = document.body.classList.contains(this.classes.pinned);
      if (pinned && headerSection) {
        const rect = headerSection.getBoundingClientRect();
        const visibleStickyHeight = Math.max(
          0,
          Math.round(rect.bottom - rect.top),
        );
        document.documentElement.style.setProperty(
          "--header-height",
          `${visibleStickyHeight}px`,
        );
      } else {
        // Fallback to measured <header> DOM height before any sticking occurs.
        document.documentElement.style.setProperty(
          "--header-height",
          `${Math.round(this.offsetHeight)}px`,
        );
      }

      this.currentScrollTop = scrollTop;
    });
  }
}

customElements.define("sticky-header", StickyHeader, { extends: "header" });

const clearDropdownCount = new WeakMap();
class DetailsDropdown extends HTMLDetailsElement {
  constructor() {
    super();
    // Initialize properties
    this.classes = { bodyClass: "has-dropdown-menu" };
    this.events = {
      handleAfterHide: "menu:handleAfterHide",
      handleAfterShow: "menu:handleAfterShow",
    };

    // Reference to first and last child elements
    this.summaryElement = this.firstElementChild;
    this.contentElement = this.lastElementChild;

    // Initial state based on attributes
    this._open = this.hasAttribute("open");

    // Event listeners for summary element
    this.summaryElement.addEventListener(
      "click",
      this.handleSummaryClick.bind(this),
    );

    if (this.trigger === "hover") {
      this.summaryElement.addEventListener("focusin", (event) => {
        if (event.target === this.summaryElement) {
          this.open = true;
        }
      });
      this.summaryElement.addEventListener("focusout", (event) => {
        if (!this.contentElement.contains(event.relatedTarget)) {
          this.open = false;
        }
      });
    }

    // Binding methods to ensure 'this' context is correct when they are called
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleEscKeyPress = this.handleEscKeyPress.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);

    // Setup hover detection with debouncing
    this.hoverTimer = null;
    this.detectHoverListener = this.detectHover.bind(this);
    this.addEventListener("mouseenter", this.detectHoverListener.bind(this));
    this.addEventListener("mouseleave", this.detectHoverListener.bind(this));
  }

  set open(value) {
    // Check if the new value is different from the current value
    if (value !== this._open) {
      // Update the internal state
      this._open = value;

      // Perform actions based on whether the element is connected to the DOM
      if (this.isConnected) {
        // If connected, perform a transition
        this.transition(value);
      } else {
        // If not connected, directly manipulate the 'open' attribute
        if (value) {
          this.setAttribute("open", "");
        } else {
          this.removeAttribute("open");
        }
      }
    }
  }

  get open() {
    return this._open;
  }

  get trigger() {
    if (this.hasAttribute("trigger")) {
      return this.getAttribute("trigger");
    } else {
      return "click";
    }
  }

  get level() {
    if (this.hasAttribute("level")) {
      return this.getAttribute("level");
    } else {
      return "top";
    }
  }

  handleSummaryClick(event) {
    // Prevent the default action of the event
    event.preventDefault();

    // Check if the device is not touch-enabled and the trigger type is 'hover'
    if (
      !FoxTheme.config.isTouch &&
      this.trigger === "hover" &&
      this.summaryElement.hasAttribute("data-link")
    ) {
      // If conditions are met, navigate to the URL specified in 'data-link'
      window.location.href = this.summaryElement.getAttribute("data-link");
    } else {
      // Otherwise, toggle the 'open' state
      this.open = !this.open;
    }
  }

  async transition(value) {
    if (value) {
      // Increment the lock count for dropdowns
      clearDropdownCount.set(
        DetailsDropdown,
        clearDropdownCount.get(DetailsDropdown) + 1,
      );

      // Add class to body and set attributes
      document.body.classList.add(this.classes.bodyClass);
      this.setAttribute("open", "");
      this.summaryElement.setAttribute("open", "");
      setTimeout(() => this.contentElement.setAttribute("open", ""), 100);

      // Add event listeners
      document.addEventListener("click", this.handleOutsideClick);
      document.addEventListener("keydown", this.handleEscKeyPress);
      document.addEventListener("focusout", this.handleFocusOut);

      // Perform the transition in animation
      await this.showWithTransition();

      // Check for reverse condition (not implemented here for brevity)
      this.needsReverse();

      // Wait for the after show event
      return FoxTheme.utils.waitForEvent(this, this.events.handleAfterShow);
    } else {
      // Decrement the lock count for dropdowns
      clearDropdownCount.set(
        DetailsDropdown,
        clearDropdownCount.get(DetailsDropdown) - 1,
      );

      // Toggle class on body based on lock count
      document.body.classList.toggle(
        this.classes.bodyClass,
        clearDropdownCount.get(DetailsDropdown) > 0,
      );

      // Remove attributes and event listeners
      this.summaryElement.removeAttribute("open");
      this.contentElement.removeAttribute("open");
      document.removeEventListener("click", this.handleOutsideClick);
      document.removeEventListener("keydown", this.handleEscKeyPress);
      document.removeEventListener("focusout", this.handleFocusOut);

      // Perform the transition out animation
      await this.hideWithTransition();

      // Conditionally remove the 'open' attribute
      if (!this.open) {
        this.removeAttribute("open");
      }

      // Wait for the after hide event
      return FoxTheme.utils.waitForEvent(this, this.events.handleAfterHide);
    }
  }

  async showWithTransition() {
    FoxTheme.Motion.animate(
      this.contentElement,
      { opacity: [0, 1], visibility: "visible" },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
        delay: FoxTheme.config.motionReduced ? 0 : 0.1,
      },
    );
    const translateY = this.level === "top" ? "-105%" : "2rem";
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      { transform: [`translateY(${translateY})`, "translateY(0)"] },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
      },
    ).finished;
  }

  async hideWithTransition() {
    FoxTheme.Motion.animate(
      this.contentElement,
      { opacity: 0, visibility: "hidden" },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.3,
        easing: [0.7, 0, 0.2, 1],
      },
    );
    const translateY = this.level === "top" ? "-105%" : "2rem";
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      { transform: `translateY(${translateY})` },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
      },
    ).finished;
  }

  handleOutsideClick(event) {
    const isClickInside = this.contains(event.target);
    const isClickOnDetailsDropdown =
      event.target.closest("details") instanceof DetailsDropdown;

    if (!isClickInside && !isClickOnDetailsDropdown) {
      this.open = false;
    }
  }

  handleEscKeyPress(event) {
    if (event.code === "Escape") {
      const targetMenu = event.target.closest("details[open]");
      if (targetMenu) {
        targetMenu.open = false;
      }
    }
  }

  handleFocusOut(event) {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.open = false;
    }
  }

  detectHover(event) {
    if (this.trigger === "hover") {
      this.open = event.type === "mouseenter";
    }
  }

  needsReverse() {
    const totalWidth =
      this.contentElement.offsetLeft + this.contentElement.clientWidth * 2;
    if (totalWidth > window.innerWidth) {
      this.contentElement.classList.add("needs-reverse");
    }
  }
}

customElements.define("details-dropdown", DetailsDropdown, {
  extends: "details",
});
clearDropdownCount.set(DetailsDropdown, 0);

class DetailsMega extends DetailsDropdown {
  constructor() {
    super();
    this.handleDocumentScroll = this.handleDocumentScroll.bind(this);
    this.handleViewportChange = this.handleViewportChange.bind(this);
    if (Shopify.designMode) {
      this.addEventListener("shopify:block:select", () => {
        this.open = true;
      });

      this.addEventListener("shopify:block:deselect", () => {
        this.open = false;
      });
    }
  }

  async transition(value) {
    const result = await super.transition(value);

    if (value) {
      this.bindActiveStateHandlers();
      this.trapMenuFocus();
    } else {
      this.unbindActiveStateHandlers();
      FoxTheme.a11y.removeTrapFocus(this.summaryElement);
    }

    return result;
  }

  async showWithTransition() {
    // Perform the animation on the first child of the content element
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      {
        visibility: "visible",
        transform: ["translateY(-100%)", "translateY(0)"],
      },
      {
        duration: 0 /*FoxTheme.config.motionReduced ? 0 : 0.5*/,
        easing: [0.39, 0.575, 0.565, 1.0],
      },
    ).finished;
  }

  async hideWithTransition() {
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      {
        visibility: "hidden",
        transform: "translateY(-100%)",
      },
      {
        duration: 0 /*FoxTheme.config.motionReduced ? 0 : 0.5*/,
        easing: [0.39, 0.575, 0.565, 1.0],
      },
    ).finished;
  }

  bindActiveStateHandlers() {
    window.addEventListener("scroll", this.handleDocumentScroll, {
      passive: true,
    });
    window.addEventListener("resize", this.handleViewportChange);
  }

  unbindActiveStateHandlers() {
    window.removeEventListener("scroll", this.handleDocumentScroll);
    window.removeEventListener("resize", this.handleViewportChange);
  }

  trapMenuFocus() {
    const focusableElements = FoxTheme.a11y.getFocusableElements(
      this.contentElement,
    );
    const firstFocusableElement = focusableElements[0] || this.summaryElement;

    if (firstFocusableElement) {
      FoxTheme.a11y.trapFocus(this.contentElement, firstFocusableElement);
    }
  }

  handleDocumentScroll() {
    if (this.open) {
      this.open = false;
    }
  }

  handleViewportChange() {
    if (window.innerWidth < 1025 && this.open) {
      this.open = false;
    }
  }
}

customElements.define("details-mega", DetailsMega, { extends: "details" });

class MenuProductList extends HTMLElement {
  constructor() {
    super();
    this.initSlide();
    this.elementToFocus = this.querySelector("button");
  }

  get container() {
    return this.querySelector(".swiper");
  }

  get sliderPagination() {
    return this.querySelector(".swiper-pagination");
  }

  get sliderNext() {
    return this.querySelector(".swiper-button-next");
  }

  get sliderPrev() {
    return this.querySelector(".swiper-button-prev");
  }

  get numberOfColumns() {
    return parseInt(this.dataset.columns);
  }

  initSlide() {
    const slider = new FoxTheme.Carousel(this.container, {
      spaceBetween: 10,
      slidesPerView: this.numberOfColumns,
      loop: false,
      pagination: {
        el: this.sliderPagination,
        type: "fraction",
      },
      navigation: {
        nextEl: this.sliderNext,
        prevEl: this.sliderPrev,
      },
      focusableElements:
        "input, select, option, textarea, button, video, label, a",
    });
    slider.init();

    const focusableElements = FoxTheme.a11y.getFocusableElements(this);

    focusableElements.forEach((element) => {
      element.addEventListener("focusin", function () {
        const slide = element.closest(".swiper-slide");

        slide && slider.slider.slideTo(slider.slider.slides.indexOf(slide));
      });
    });
  }
}
customElements.define("menu-product-list", MenuProductList);

const sairaHeaderMegaMenus = new WeakMap();

class SairaHeaderMegaMenu {
  constructor(header) {
    this.header = header;
    this.desktopButton = header.querySelector("[data-saira-shop-toggle]");
    this.desktopMenu = header.querySelector("[data-saira-mega-menu]");
    this.desktopCaret = header.querySelector("[data-saira-shop-caret]");
    this.mobileButton = header.querySelector("[data-saira-mobile-toggle]");
    this.mobileMenu = header.querySelector("[data-saira-mobile-menu]");
    this.mobileOpenIcon = header.querySelector("[data-saira-mobile-open-icon]");
    this.mobileCloseIcon = header.querySelector(
      "[data-saira-mobile-close-icon]",
    );
    this.menuLinks = header.querySelectorAll("[data-saira-menu-link]");
    this.focusReturnElement = null;

    if (
      (!this.desktopButton || !this.desktopMenu) &&
      (!this.mobileButton || !this.mobileMenu)
    ) {
      return;
    }

    this.handleDesktopButtonClick = this.handleDesktopButtonClick.bind(this);
    this.handleMobileButtonClick = this.handleMobileButtonClick.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
    this.handleWindowScroll = this.handleWindowScroll.bind(this);
    this.handleWindowResize = this.handleWindowResize.bind(this);
    this.handleMenuLinkClick = this.handleMenuLinkClick.bind(this);

    this.desktopButton?.addEventListener(
      "click",
      this.handleDesktopButtonClick,
    );
    this.mobileButton?.addEventListener("click", this.handleMobileButtonClick);
    document.addEventListener("click", this.handleDocumentClick);
    document.addEventListener("keydown", this.handleDocumentKeydown);
    window.addEventListener("scroll", this.handleWindowScroll, {
      passive: true,
    });
    window.addEventListener("resize", this.handleWindowResize);
    this.menuLinks.forEach((link) =>
      link.addEventListener("click", this.handleMenuLinkClick),
    );
    this.updateMobileIcon(false);
  }

  get isOpen() {
    return (
      this.desktopMenu?.dataset.open === "true" ||
      this.mobileMenu?.dataset.open === "true"
    );
  }

  get isDesktopViewport() {
    return window.innerWidth >= 1025;
  }

  get activeButton() {
    return this.isDesktopViewport ? this.desktopButton : this.mobileButton;
  }

  get activeMenu() {
    return this.isDesktopViewport ? this.desktopMenu : this.mobileMenu;
  }

  updateControlState(control, isOpen) {
    if (!control) return;

    control.dataset.open = isOpen ? "true" : "false";
    control.setAttribute("aria-expanded", isOpen ? "true" : "false");
  }

  updatePanelState(panel, isOpen) {
    if (!panel) return;

    panel.dataset.open = isOpen ? "true" : "false";
    panel.setAttribute("aria-hidden", isOpen ? "false" : "true");
    panel.inert = !isOpen;
  }

  updateMobileIcon(isOpen) {
    if (this.mobileOpenIcon) {
      this.mobileOpenIcon.classList.toggle("hidden!", isOpen);
    }

    if (this.mobileCloseIcon) {
      this.mobileCloseIcon.classList.toggle("hidden", !isOpen);
    }
  }

  setOpen(value, options = {}) {
    const { restoreFocus = true } = options;
    const isOpen = Boolean(value);
    const showDesktopMenu = isOpen && this.isDesktopViewport;
    const showMobileMenu = isOpen && !this.isDesktopViewport;

    this.updatePanelState(this.desktopMenu, showDesktopMenu);
    this.updatePanelState(this.mobileMenu, showMobileMenu);
    this.updateControlState(this.desktopButton, showDesktopMenu);
    this.updateControlState(this.mobileButton, showMobileMenu);
    this.updateMobileIcon(showMobileMenu);

    if (this.desktopCaret) {
      this.desktopCaret.dataset.open = showDesktopMenu ? "true" : "false";
    }

    document.body.style.overflow = showMobileMenu ? "hidden" : "";

    if (isOpen) {
      this.focusReturnElement = this.activeButton;

      const focusableElements = FoxTheme.a11y.getFocusableElements(
        this.activeMenu,
      );
      const firstFocusableElement =
        focusableElements[0] || this.focusReturnElement;

      if (firstFocusableElement) {
        FoxTheme.a11y.trapFocus(this.activeMenu, firstFocusableElement);
      }
    } else {
      FoxTheme.a11y.removeTrapFocus(
        this.focusReturnElement || this.activeButton || this.desktopButton,
      );

      if (restoreFocus && this.focusReturnElement) {
        this.focusReturnElement.focus();
      }
    }
  }

  handleDesktopButtonClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.isDesktopViewport) return;

    this.setOpen(!this.isOpen, { restoreFocus: false });
  }

  handleMobileButtonClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.isDesktopViewport) return;

    this.setOpen(!this.isOpen, { restoreFocus: false });
  }

  handleDocumentClick(event) {
    if (!this.isOpen) return;

    const isClickInsideDesktopMenu = this.desktopMenu?.contains(event.target);
    const isClickInsideMobileMenu = this.mobileMenu?.contains(event.target);
    const isClickOnDesktopButton = this.desktopButton?.contains(event.target);
    const isClickOnMobileButton = this.mobileButton?.contains(event.target);

    if (
      !isClickInsideDesktopMenu &&
      !isClickInsideMobileMenu &&
      !isClickOnDesktopButton &&
      !isClickOnMobileButton
    ) {
      this.setOpen(false, { restoreFocus: false });
    }
  }

  handleDocumentKeydown(event) {
    if (event.key === "Escape" && this.isOpen) {
      this.setOpen(false);
    }
  }

  handleWindowScroll() {
    if (this.desktopMenu?.dataset.open === "true") {
      this.setOpen(false, { restoreFocus: false });
    }
  }

  handleWindowResize() {
    if (this.isOpen) {
      this.setOpen(false, { restoreFocus: false });
    }
  }

  handleMenuLinkClick() {
    this.setOpen(false, { restoreFocus: false });
  }
}

function initSairaHeaderMegaMenus(root = document) {
  const headers = root.matches?.("header[is]")
    ? [root]
    : root.querySelectorAll("header[is]");

  headers.forEach((header) => {
    if (
      header.querySelector("[data-saira-shop-toggle]") &&
      !sairaHeaderMegaMenus.has(header)
    ) {
      sairaHeaderMegaMenus.set(header, new SairaHeaderMegaMenu(header));
    }
  });
}

initSairaHeaderMegaMenus();

document.addEventListener("shopify:section:load", (event) => {
  initSairaHeaderMegaMenus(event.target);
});
